"use client"

import React, { useRef, useEffect, useCallback } from "react"
import { useState } from "react"
import { X, Clock, MapPin, Search, Navigation } from "lucide-react"
import { Dialog, Flex, Box, Heading, Text, TextField, Select, Button, TextArea, ScrollArea, Checkbox } from "@radix-ui/themes"
import ImageUpload from "./ImageUpload"
import { useLanguage } from "../contexts/LanguageContext"
import { sudanStates, sudanCitiesByState } from "../utils/sudanLocations"

interface Restaurant {
  _id?: string
  name: string
  description: string
  category: string
  address: {
    street: string
    city: string
    state: string
    zipCode: string
    coordinates: {
      latitude: number
      longitude: number
    }
  }
  contact?: {
    phone?: string
    email?: string
  }
  phone?: string
  email?: string
  image: string
  deliveryInfo?: {
    fee: number
    minimumOrder: number
    estimatedTime: string
    radius: number
  }
  deliveryTime?: string
  deliveryFee?: number
  minimumOrder?: number
  operatingHours: {
    [key: string]: { open: string; close: string; isOpen: boolean }
  }
}

interface RestaurantModalProps {
  restaurant?: Restaurant | null
  onClose: () => void
  onSave: (data: any) => void
}

const RestaurantModal: React.FC<RestaurantModalProps> = ({ restaurant, onClose, onSave }) => {
  const { language } = useLanguage()
  const [formData, setFormData] = useState<{
    name: string
    description: string
    category: string
    address: {
      street: string
      city: string
      state: string
      zipCode: string
      coordinates: {
        latitude: number
        longitude: number
      }
    }
    phone: string
    email: string
    image: string
    deliveryTime: string
    deliveryFee: number
    minimumOrder: number
    deliveryRadius: number
    operatingHours: {
      [key: string]: { open: string; close: string; isOpen: boolean }
    }
  }>({
    name: "",
    description: "",
    category: "",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      coordinates: {
        latitude: 0,
        longitude: 0
      }
    },
    phone: "",
    email: "",
    image: "",
    deliveryTime: "30-45 mins",
    deliveryFee: 2.99,
    minimumOrder: 15,
    deliveryRadius: 5,
    operatingHours: {
      monday: { open: "09:00", close: "22:00", isOpen: true },
      tuesday: { open: "09:00", close: "22:00", isOpen: true },
      wednesday: { open: "09:00", close: "22:00", isOpen: true },
      thursday: { open: "09:00", close: "22:00", isOpen: true },
      friday: { open: "09:00", close: "23:00", isOpen: true },
      saturday: { open: "09:00", close: "23:00", isOpen: true },
      sunday: { open: "10:00", close: "21:00", isOpen: true },
    },
  })

  // Updated categories to match backend enum values
  const categories = [
    { label: "Fast food", value: "Fast food" },
    { label: "Pizza", value: "Pizza" },
    { label: "Asian", value: "Asian" },
    { label: "Middle Eastern", value: "Middle-eastern" },
    { label: "Desserts", value: "Desserts" },
    { label: "Healthy", value: "Healthy" },
    { label: "Coffee", value: "Coffee" }
  ]

  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

  // Map state and refs
  const [mapLoaded, setMapLoaded] = useState(false)
  const [placeSearch, setPlaceSearch] = useState("")
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searching, setSearching] = useState(false)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const autocompleteServiceRef = useRef<any>(null)
  const placesServiceRef = useRef<any>(null)

  // Initialize Google Maps
  useEffect(() => {
    const w = window as any
    if (w.google?.maps?.Map) {
      setMapLoaded(true)
      return
    }

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
    if (!apiKey) return

    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (w.google?.maps?.Map) {
          setMapLoaded(true)
          clearInterval(checkLoaded)
        }
      }, 100)
      setTimeout(() => clearInterval(checkLoaded), 10000)
    }
  }, [])

  // Create map when loaded
  useEffect(() => {
    if (!mapLoaded || !mapContainerRef.current || mapInstanceRef.current) return

    const w = window as any
    if (!w.google?.maps?.Map) return

    try {
      // Default to Khartoum, Sudan or existing coordinates
      const defaultCenter = formData.address.coordinates.latitude !== 0
        ? { lat: formData.address.coordinates.latitude, lng: formData.address.coordinates.longitude }
        : { lat: 15.5007, lng: 32.5599 }

      mapInstanceRef.current = new w.google.maps.Map(mapContainerRef.current, {
        center: defaultCenter,
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      })

      // Add draggable marker
      markerRef.current = new w.google.maps.Marker({
        position: defaultCenter,
        map: mapInstanceRef.current,
        draggable: true,
        icon: {
          path: w.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: "#EF4444",
          fillOpacity: 1,
          strokeColor: "#FFFFFF",
          strokeWeight: 3,
        },
      })

      // Initialize Places services
      autocompleteServiceRef.current = new w.google.maps.places.AutocompleteService()
      placesServiceRef.current = new w.google.maps.places.PlacesService(mapInstanceRef.current)

      // Update coordinates when marker is dragged
      markerRef.current.addListener("dragend", () => {
        const pos = markerRef.current.getPosition()
        updateCoordinatesFromPosition(pos.lat(), pos.lng())
      })

      // Also allow clicking on map to move marker
      mapInstanceRef.current.addListener("click", (e: any) => {
        const lat = e.latLng.lat()
        const lng = e.latLng.lng()
        markerRef.current.setPosition({ lat, lng })
        updateCoordinatesFromPosition(lat, lng)
      })
    } catch (err) {
      console.error("Failed to initialize map:", err)
    }
  }, [mapLoaded])

  // Update coordinates and reverse geocode
  const updateCoordinatesFromPosition = useCallback((lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        coordinates: { latitude: lat, longitude: lng }
      }
    }))

    // Reverse geocode to get address
    const w = window as any
    if (w.google?.maps?.Geocoder) {
      const geocoder = new w.google.maps.Geocoder()
      geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
        if (status === "OK" && results[0]) {
          const result = results[0]
          const addressComponents = result.address_components || []

          let street = ""
          let city = ""
          let state = ""
          let zipCode = ""

          for (const component of addressComponents) {
            const types = component.types
            if (types.includes("street_number") || types.includes("route")) {
              street += (street ? " " : "") + component.long_name
            }
            if (types.includes("locality") || types.includes("administrative_area_level_2")) {
              city = component.long_name
            }
            if (types.includes("administrative_area_level_1")) {
              state = component.long_name
            }
            if (types.includes("postal_code")) {
              zipCode = component.long_name
            }
          }

          setFormData(prev => ({
            ...prev,
            address: {
              ...prev.address,
              street: street || prev.address.street,
              city: city || prev.address.city,
              state: state || prev.address.state,
              zipCode: zipCode || prev.address.zipCode,
            }
          }))
        }
      })
    }
  }, [])

  // Search for places
  const handlePlaceSearch = useCallback((query: string) => {
    setPlaceSearch(query)
    if (!query.trim() || !autocompleteServiceRef.current) {
      setSearchResults([])
      return
    }

    setSearching(true)
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: query,
        types: ["establishment", "geocode"],
        componentRestrictions: { country: "sd" }, // Sudan
      },
      (predictions: any, status: any) => {
        setSearching(false)
        if (status === "OK" && predictions) {
          setSearchResults(predictions.slice(0, 5))
        } else {
          setSearchResults([])
        }
      }
    )
  }, [])

  // Select a place from search results
  const handleSelectPlace = useCallback((placeId: string) => {
    if (!placesServiceRef.current) return

    placesServiceRef.current.getDetails(
      { placeId, fields: ["geometry", "formatted_address", "address_components", "name"] },
      (place: any, status: any) => {
        if (status === "OK" && place?.geometry?.location) {
          const lat = place.geometry.location.lat()
          const lng = place.geometry.location.lng()

          // Move map and marker
          if (mapInstanceRef.current && markerRef.current) {
            mapInstanceRef.current.setCenter({ lat, lng })
            mapInstanceRef.current.setZoom(17)
            markerRef.current.setPosition({ lat, lng })
          }

          // Update form data
          const addressComponents = place.address_components || []
          let street = place.name || ""
          let city = ""
          let state = ""
          let zipCode = ""

          for (const component of addressComponents) {
            const types = component.types
            if (types.includes("locality") || types.includes("administrative_area_level_2")) {
              city = component.long_name
            }
            if (types.includes("administrative_area_level_1")) {
              state = component.long_name
            }
            if (types.includes("postal_code")) {
              zipCode = component.long_name
            }
          }

          setFormData(prev => ({
            ...prev,
            address: {
              ...prev.address,
              street: street,
              city: city || prev.address.city,
              state: state || prev.address.state,
              zipCode: zipCode || prev.address.zipCode || "00000",
              coordinates: { latitude: lat, longitude: lng }
            }
          }))

          setPlaceSearch("")
          setSearchResults([])
        }
      }
    )
  }, [])

  useEffect(() => {
    if (restaurant) {
      setFormData({
        name: restaurant.name || "",
        description: restaurant.description || "",
        category: restaurant.category || "",
        address: restaurant.address || {
          street: "",
          city: "",
          state: "",
          zipCode: "",
          coordinates: {
            latitude: 0,
            longitude: 0
          }
        },
        phone: restaurant.phone || restaurant.contact?.phone || "",
        email: restaurant.email || restaurant.contact?.email || "",
        image: restaurant.image || "",
        deliveryTime: restaurant.deliveryTime || restaurant.deliveryInfo?.estimatedTime || "30-45 mins",
        deliveryFee: restaurant.deliveryFee || restaurant.deliveryInfo?.fee || 2.99,
        minimumOrder: restaurant.minimumOrder || restaurant.deliveryInfo?.minimumOrder || 15,
        deliveryRadius: restaurant.deliveryInfo?.radius || 5,
        operatingHours: restaurant.operatingHours || {
          monday: { open: "09:00", close: "22:00", isOpen: true },
          tuesday: { open: "09:00", close: "22:00", isOpen: true },
          wednesday: { open: "09:00", close: "22:00", isOpen: true },
          thursday: { open: "09:00", close: "22:00", isOpen: true },
          friday: { open: "09:00", close: "23:00", isOpen: true },
          saturday: { open: "09:00", close: "23:00", isOpen: true },
          sunday: { open: "10:00", close: "21:00", isOpen: true },
        },
      })
    }
  }, [restaurant])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Transform the form data to match backend schema
    const transformedData = {
      name: formData.name,
      description: formData.description,
      category: formData.category,
      image: formData.image,
      address: formData.address,
      contact: {
        phone: formData.phone,
        email: formData.email,
      },
      deliveryInfo: {
        fee: formData.deliveryFee,
        minimumOrder: formData.minimumOrder,
        estimatedTime: formData.deliveryTime,
        radius: formData.deliveryRadius,
      },
      operatingHours: formData.operatingHours,
    }

    onSave(transformedData)
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleAddressChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      }
    }))
  }

  const handleOperatingHoursChange = (day: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          [field]: value,
        },
      },
    }))
  }

  return (
    <Dialog.Root open={true} onOpenChange={onClose}>
      <Dialog.Content style={{ maxWidth: 900 }}>
        <Dialog.Title>
          {restaurant ? "Edit Restaurant" : "Add New Restaurant"}
        </Dialog.Title>

        <Dialog.Description size="2" mb="4">
          {restaurant ? "Update restaurant information" : "Create a new restaurant"}
        </Dialog.Description>

        <ScrollArea style={{ maxHeight: "70vh" }} scrollbars="vertical">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Basic Information</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Restaurant Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category *</label>
                  <select
                    required
                    value={formData.category}
                    onChange={(e) => handleInputChange("category", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Street Address *</label>
                  <input
                    type="text"
                    required
                    value={formData.address.street}
                    onChange={(e) => handleAddressChange("street", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State *</label>
                    <select
                      required
                      value={formData.address.state}
                      onChange={(e) => {
                        handleAddressChange("state", e.target.value)
                        handleAddressChange("city", "")
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select State</option>
                      {sudanStates.map(state => (
                        <option key={state.value} value={state.value}>
                          {language === 'ar' ? state.labelAr : state.labelEn}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City *</label>
                    <select
                      required
                      value={formData.address.city}
                      onChange={(e) => handleAddressChange("city", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white bg-white dark:bg-gray-700"
                      disabled={!formData.address.state}
                    >
                      <option value="">Select City</option>
                      {(sudanCitiesByState[formData.address.state] || []).map(city => (
                        <option key={city.value} value={city.value}>
                          {language === 'ar' ? city.labelAr : city.labelEn}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ZIP Code *</label>
                  <input
                    type="text"
                    required
                    value={formData.address.zipCode}
                    onChange={(e) => handleAddressChange("zipCode", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Location Map Section */}
                <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Restaurant Location
                  </h4>

                  {/* Place Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={placeSearch}
                      onChange={(e) => handlePlaceSearch(e.target.value)}
                      placeholder="Search for business or address..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    {searching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}

                    {/* Search Results Dropdown */}
                    {searchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {searchResults.map((result) => (
                          <button
                            key={result.place_id}
                            type="button"
                            onClick={() => handleSelectPlace(result.place_id)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-start gap-2"
                          >
                            <MapPin className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {result.structured_formatting?.main_text || result.description}
                              </p>
                              <p className="text-xs text-gray-500">
                                {result.structured_formatting?.secondary_text || ""}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Map Container */}
                  <div
                    ref={mapContainerRef}
                    className="w-full h-[200px] rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-200 dark:bg-gray-700"
                  >
                    {!mapLoaded && (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                          <p className="text-sm text-gray-500">Loading map...</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Coordinates Display */}
                  {formData.address.coordinates.latitude !== 0 && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <Navigation className="w-3 h-3" />
                      Location set: {formData.address.coordinates.latitude.toFixed(6)}, {formData.address.coordinates.longitude.toFixed(6)}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Click on the map or search for a business to set the location</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <ImageUpload
                  currentImage={formData.image}
                  onImageUploaded={(url) => handleInputChange("image", url)}
                  uploadEndpoint={restaurant?._id ? `restaurant/${restaurant._id}/logo` : 'restaurant/:id/logo'}
                  entityId={restaurant?._id}
                  label="Restaurant Logo"
                  maxSize={5}
                  disabled={!restaurant?._id}
                />

                {!restaurant?._id && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Image URL (Temporary)
                    </label>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) => handleInputChange("image", e.target.value)}
                      placeholder="Enter image URL or save restaurant first to upload"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                )}
              </div>

              {/* Delivery Settings & Operating Hours */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Delivery Settings</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Delivery Time</label>
                  <input
                    type="text"
                    value={formData.deliveryTime}
                    onChange={(e) => handleInputChange("deliveryTime", e.target.value)}
                    placeholder="e.g., 30-45 mins"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Delivery Fee ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.deliveryFee}
                    onChange={(e) => handleInputChange("deliveryFee", Number.parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Minimum Order ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.minimumOrder}
                    onChange={(e) => handleInputChange("minimumOrder", Number.parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Delivery Radius (km)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.deliveryRadius}
                    onChange={(e) => handleInputChange("deliveryRadius", Number.parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Operating Hours
                  </h4>
                  <div className="space-y-3">
                    {days.map((day) => (
                      <div key={day} className="flex items-center gap-3">
                        <div className="w-20 text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{day}</div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.operatingHours[day].isOpen}
                            onChange={(e) => handleOperatingHoursChange(day, "isOpen", e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-400">Open</span>
                        </label>
                        {formData.operatingHours[day].isOpen && (
                          <>
                            <input
                              type="time"
                              value={formData.operatingHours[day].open}
                              onChange={(e) => handleOperatingHoursChange(day, "open", e.target.value)}
                              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                            />
                            <span className="text-gray-500">to</span>
                            <input
                              type="time"
                              value={formData.operatingHours[day].close}
                              onChange={(e) => handleOperatingHoursChange(day, "close", e.target.value)}
                              className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <Flex gap="3" mt="6" pt="6" justify="end" style={{ borderTop: "1px solid var(--gray-5)" }}>
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit">
                {restaurant ? "Update Restaurant" : "Create Restaurant"}
              </Button>
            </Flex>
          </form>
        </ScrollArea>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default RestaurantModal
